import React, { useState } from 'react'
import { NavigationStackScreenComponent } from 'react-navigation-stack'
import { CustomHeader } from '../../components/CustomHeader'
import { Coordinator } from '../../navigation/coordinator/coordinator'
import styled from 'styled-components/native'
import { atlTheme } from '../../atlTheme'
import {
  ATLButton,
  ATLCheckbox,
  ATLSpacing,
  ATLTextHead,
  ButtonTypeEnum,
  CheckboxText,
  KeyboardTypeEnum,
  SpacingSizesEnum,
  TextAlignmentEnum,
  TextCaseEnum,
} from '@appsimples/atlantica-react-native'
import { useForm } from 'react-simple-hooks-forms/dist'
import { buildValidatorFromConstraints } from '../../utils/buildValidatorFromConstraints'
import { CPF_CONSTRAINT, EMAIL_CONSTRAINT } from '../../constants/formsConstraints'
import { formatMessage } from '../../constants/strings/intl/intlHooks'
import { messages } from '../../constants/strings/messages'
import { CPF_LENGTH } from '../../constants/lengths'
import { ATLTextInputDocumentMaskField } from '../../components/form/ATLTextInputCPFMaskField'
import { ATLTextInputField } from '../../components/form/ATLTextInputField'
import { FormCompletenessValidator } from '../../components/form/FormCompletenessValidator'
import { usePromise } from '../../utils/hooks/usePromise'
import { signUp } from '../../services/auth/signUp'
import { showErrorSnackbar } from '../../components/snackbar/Snackbar'
import { SignUpInput } from '../../services/GlobalTypes'
import { Loading } from '../../components/loading/Loading'
import { Colors } from '../../constants/colors'
import { GenericInfoScreenContext } from '../ProfileScreens/constants'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import { onlyNumberString } from '../../utils/formatters'
import { TouchableOpacity } from 'react-native'
import { openWhatsApp } from '../../utils/openWhatsApp'
import { BRIO_SAC_WHATSAPP_NUMBER, whatsAppTextContactUs } from '../../constants/whatsAppInfo'
import { platformConfig } from '../../constants/platform'

const LoadingButtonContainer = styled.View`
  margin-vertical: ${atlTheme.SpacingBase}px;
  margin-horizontal: ${atlTheme.SpacingDistribute}px;
  height: ${atlTheme.HeightButtonBase}px;
  background-color: ${atlTheme.ColorBrand1Default};
  border-radius: ${atlTheme.BorderRadiusFloatingButton}px;
  justify-content: center;
`
const Container = styled.SafeAreaView`
  margin-top: ${atlTheme.SpacingAtomic}px;
  flex: 1;
  background: #ffffff;
  width: 100%;
  padding-top: ${atlTheme.SpacingMarginal}px;
`
const LabelContainer = styled.Text`
  flex-direction: row;
  align-items: flex-start;
  font-family: ${atlTheme.FontFamilyBrand};
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
  display: flex;
  align-items: flex-end;
  color: ${atlTheme.ColorHighContrast};
  margin: 0px ${atlTheme.SpacingMarginal}px;
  padding-top: ${atlTheme.SpacingMarginal}px;
`
const BrandColoredText = styled.Text`
  color: ${atlTheme.ColorBrand1Default};
`
const ButtonWrapper = styled.View`
  position: relative;
  bottom: ${atlTheme.SpacingBase}px;
  width: 100%;
`

const ContactUsLabel = styled.Text`
  font-family: ${atlTheme.FontFamilyBrand};
  font-weight: ${atlTheme.FontWeight500};
  font-size: ${atlTheme.FontSizeAuxiliary}px;
  line-height: ${atlTheme.LineHeightHeading}px;
  text-transform: uppercase;
  color: ${atlTheme.ColorMediumContrast};
  font-size: ${atlTheme.FontSizeBody};
`
const ContactUsContainer = styled.View`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-bottom: ${atlTheme.SpacingMarginal}px;
`
const ContactUsActionLabel = styled.Text`
  font-family: ${atlTheme.FontFamilyBrand};
  font-weight: ${atlTheme.FontWeight500};
  font-size: ${atlTheme.FontSizeAuxiliary}px;
  line-height: ${atlTheme.LineHeightHeading}px;
  text-transform: uppercase;
  color: ${atlTheme.ColorBrand2Default};
  margin-left: ${atlTheme.SpacingAtomic}px;
  font-size: ${atlTheme.FontSizeBody};
`

const formName = 'SignUpScreen'

const formFields = {
  EMAIL: 'email',
  CPF: 'cpf',
}

const constraints = {
  [formFields.CPF]: CPF_CONSTRAINT,
  [formFields.EMAIL]: EMAIL_CONSTRAINT,
}

export const SignUpScreen: NavigationStackScreenComponent = () => {
  const [isUseTermsToggleChecked, setIsUseTermsToggleChecked] = useState<boolean>()
  const { Form, submit } = useForm({ validator: buildValidatorFromConstraints(constraints), formName })

  const onPressUseTerms = () => {
    Coordinator.goToGenericInfoScreen(GenericInfoScreenContext.USE_TERMS, true)
  }

  const onPressPrivacyPolicy = () => {
    Coordinator.goToGenericInfoScreen(GenericInfoScreenContext.PRIVACY_POLICY, true)
  }

  const checkboxTexts: CheckboxText[] = [
    { text: formatMessage(messages.signUp_checkbox_1) },
    { text: formatMessage(messages.signUp_checkbox_2), onPress: onPressUseTerms },
    { text: formatMessage(messages.signUp_checkbox_3) },
    { text: formatMessage(messages.signUp_checkbox_4), onPress: onPressPrivacyPolicy },
  ]

  const onRequestSuccess = () => {
    Coordinator.goToSignUpVerification()
  }

  const onRequestError = () => {
    showErrorSnackbar(formatMessage(messages.signUp_error))
  }

  const { request: doSignUp, loading: isSigningUp } = usePromise(signUp, [], {
    onSuccess: onRequestSuccess,
    onError: onRequestError,
  })

  const onSuccess = (input: SignUpInput) => {
    doSignUp({
      cpf: onlyNumberString(input.cpf),
      email: input.email,
    })
  }

  const onError = err => {
    console.tron.log('Error while validating sign up: ', err)
  }

  const onPressSubmit = async () => {
    await submit(onSuccess, onError)
  }

  const onPressContactUs = () => {
    openWhatsApp(whatsAppTextContactUs, BRIO_SAC_WHATSAPP_NUMBER)
  }

  const toggleCheckbox = () => {
    setIsUseTermsToggleChecked(!isUseTermsToggleChecked)
  }

  return (
    <Container>
      <Form>
        <KeyboardAwareScrollView scrollEnabled>
          <LabelContainer>
            {formatMessage(messages.signUp_headerMain)}
            <BrandColoredText>{formatMessage(messages.signUp_headerSecondary)}</BrandColoredText>.
          </LabelContainer>
          <ATLTextHead textAlignment={TextAlignmentEnum.left}>{formatMessage(messages.signUp_subtitle)}</ATLTextHead>
          <ATLSpacing type={SpacingSizesEnum.base} />
          <ATLTextInputField
            name={formFields.EMAIL}
            title={formatMessage(messages.signUp_emailLabel)}
            textInputBaseProps={{
              focusColor: atlTheme.ColorMediumContrast,
              leftIconColor: atlTheme.ColorBrand2Default,
            }}
            horizontalMargin={atlTheme.SpacingMarginal}
          />
          <ATLTextInputDocumentMaskField
            verticalMargin={atlTheme.SpacingMarginal}
            name={formFields.CPF}
            title={formatMessage(messages.signUp_cpfLabel)}
            keyboardType={platformConfig.isWeb() ? undefined : KeyboardTypeEnum.numeric}
            maxLength={CPF_LENGTH}
            horizontalMargin={atlTheme.SpacingMarginal}
          />
          <ATLCheckbox
            value={isUseTermsToggleChecked}
            onChange={toggleCheckbox}
            texts={checkboxTexts}
            buttonToggleBaseProps={{
              tintColor: atlTheme.ColorBrand2Default,
            }}
            linkLabelBaseProps={{
              textCase: TextCaseEnum.none,
              fontSize: atlTheme.FontSizeBody,
            }}
          />
        </KeyboardAwareScrollView>
        <ContactUsContainer>
          <ContactUsLabel>{formatMessage(messages.signUpContactUs_haveProblem)}</ContactUsLabel>
          <TouchableOpacity onPress={onPressContactUs}>
            <ContactUsActionLabel>{formatMessage(messages.signUpContactUs_button)}</ContactUsActionLabel>
          </TouchableOpacity>
        </ContactUsContainer>
        <ButtonWrapper>
          {isSigningUp ? (
            <LoadingButtonContainer>
              <Loading color={Colors.white} size={'small'} />
            </LoadingButtonContainer>
          ) : (
            <FormCompletenessValidator requiredFormFields={Object.values(formFields)} formName={formName}>
              {isDisabled => (
                <ATLButton
                  title={formatMessage(messages.signUp_actionButtonLabel)}
                  onPress={onPressSubmit}
                  type={ButtonTypeEnum.outlined}
                  buttonBaseProps={{
                    borderRadius: atlTheme.BorderRadius100,
                  }}
                  disabled={isDisabled || !isUseTermsToggleChecked}
                />
              )}
            </FormCompletenessValidator>
          )}
        </ButtonWrapper>
      </Form>
    </Container>
  )
}

SignUpScreen.navigationOptions = CustomHeader(formatMessage(messages.signUp_appHeaderTitle), true, () =>
  Coordinator.goBack()
)
